import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { post, put, del, MICROSERVICES } from 'api';
import { IIdentity } from 'interfaces/Identity';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { openAlert } from 'modules/alertHandler';
import { listIdentitiesAction } from 'modules/identityList';

import endpoints from './endpoints';
import { IIdentityState, IUpdateIdentityPayload } from './interfaces';

const initialState: IIdentityState = {
  errorMessage: '',
  loading: REQUEST_LIFE_CICLE.IDLE,
  isCreating: false,
};

const identityFulfilledAction = createAsyncThunk(
  'identityFulfilled',
  (payload, { dispatch }) => {
    dispatch(setIsCreating(false));
    dispatch(listIdentitiesAction({}));
  },
);

export const createIdentityAction = createAsyncThunk<
  void,
  IIdentity,
  { rejectValue: IMyKnownError }
>('createIdentity', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await post<IIdentity>(MICROSERVICES.AUTH, {
      url: endpoints.createIdentity(),
      data: {
        identity: payload.identity,
        policies: payload.policies,
        password: payload.password,
      },
    });
    dispatch(
      openAlert({
        title: 'Sucesso!',
        content: 'Usuário criado com sucesso',
        primaryButton: 'Ok',
        primaryButtonAction: identityFulfilledAction(),
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at create user', error);
    return rejectWithValue(error.response.data);
  }
});

export const updateIdentityAction = createAsyncThunk<
  void,
  IUpdateIdentityPayload,
  { rejectValue: IMyKnownError }
>('updateIdentity', async ({ id, identity }, { dispatch, rejectWithValue }) => {
  try {
    await put<IIdentity>(MICROSERVICES.AUTH, {
      url: endpoints.updateIdentity(id),
      data: {
        identity: identity.identity,
        policies: identity.policies,
        password: identity.password,
      },
    });
    dispatch(
      openAlert({
        title: 'Sucesso!',
        content: 'Usuário editado com sucesso',
        primaryButton: 'Ok',
        primaryButtonAction: identityFulfilledAction(),
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at update profile', error);
    return rejectWithValue(error.response.data);
  }
});

const confirmDeleteIdentityAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>('deleteIdentity', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await del(MICROSERVICES.AUTH, {
      url: endpoints.deleteIdentity(payload),
    });
    dispatch(
      openAlert({
        title: 'Sucesso!',
        content: 'Usuário removido com sucesso',
        primaryButton: 'Ok',
        primaryButtonAction: identityFulfilledAction(),
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at update profile', error);
    return rejectWithValue(error.response.data);
  }
});

export const deleteIdentityAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>('deleteIdentity', async (payload, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      openAlert({
        title: 'Remover usuário',
        content: 'Você deseja realmente remover esse usuário?',
        primaryButton: 'Remover',
        primaryButtonAction: confirmDeleteIdentityAction(payload),
        secondaryButton: 'Cancelar',
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at update profile', error);
    return rejectWithValue(error.response.data);
  }
});

const identitySlice = createSlice({
  name: 'identity',
  initialState,
  reducers: {
    clearIdentity: () => initialState,
    setIsCreating: (state, action: PayloadAction<boolean, string>) => {
      state.isCreating = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createIdentityAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(createIdentityAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(createIdentityAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || 'Erro no servidor';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(updateIdentityAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(updateIdentityAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(updateIdentityAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || 'Erro no servidor';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });
  },
});

export const { setIsCreating, clearIdentity } = identitySlice.actions;
export default identitySlice.reducer;
